import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, IconButton, Tooltip, Avatar, TextField, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Badge, LinearProgress, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Settings as SettingsIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Timer as TimerIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { servicesService } from '@services/servicesService';

interface Service {
  id: string;
  name: string;
  category: string;
  durationMin: number;
  price: number;
  vat: number;
  active: boolean;
  eligibleDoctorIds: string[];
  eligibleLocationIds: string[];
  description?: string;
  createdAt: string;
  updatedAt: string;
}

interface ServicePackage {
  id: string;
  name: string;
  items: Array<{
    serviceId: string;
    serviceName: string;
    qty: number;
    price: number;
  }>;
  totalPrice: number;
  discount: number;
  active: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`services-tabpanel-${index}`}
      aria-labelledby={`services-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ServicesManagement: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState<Service[]>([]);
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Incarcare servicii cu API real
  useEffect(() => {
    const loadServices = async () => {
      if (!user?.clinicId) return;
      
      setLoading(true);
      
      try {
        const servicesResponse = await servicesService.getServices({
          clinicId: user.clinicId,
          category: selectedCategory === 'all' ? undefined : selectedCategory,
          isActive: selectedStatus === 'active' ? true : selectedStatus === 'inactive' ? false : undefined
        });
        
        // Extragem array-ul de servicii din raspuns
        const servicesArray = servicesResponse.services || [];
        
        // Transformam datele in formatul componentei
        const transformedServices: Service[] = servicesArray.map((srv: any) => ({
          id: srv.id,
          name: srv.name || '',
          category: srv.category || 'Altele',
          durationMin: srv.duration || srv.durationMin || 30,
          price: srv.basePrice || srv.price || 0,
          vat: srv.vat || 19,
          active: srv.active !== undefined ? srv.active : srv.status === 'active',
          eligibleDoctorIds: srv.eligibleDoctorIds || [],
          eligibleLocationIds: srv.eligibleLocationIds || [],
          description: srv.description || '',
          createdAt: srv.createdAt || srv.created_at || new Date().toISOString(),
          updatedAt: srv.updatedAt || srv.updated_at || new Date().toISOString()
        }));
        
        setServices(transformedServices);
        
      } catch (error) {
        console.error('Eroare la incarcarea serviciilor:', error);
        setServices([]);
      }

      try {
        const packagesResponse = await servicesService.getServicePackages(user.clinicId);
        
        // Extragem array-ul de pachete din raspuns
        const packagesArray = Array.isArray(packagesResponse) ? packagesResponse : ((packagesResponse as any)?.packages || []);
        
        // Transformam datele in formatul componentei
        const transformedPackages: ServicePackage[] = packagesArray.map((pkg: any) => ({
          id: pkg.id,
          name: pkg.name || '',
          items: pkg.services || pkg.items || [],
          totalPrice: pkg.totalPrice || 0,
          discount: pkg.discount || 0,
          active: pkg.active !== undefined ? pkg.active : true,
          description: pkg.description || '',
          createdAt: pkg.createdAt || pkg.created_at || new Date().toISOString(),
          updatedAt: pkg.updatedAt || pkg.updated_at || new Date().toISOString()
        }));
        
        setPackages(transformedPackages);
        
      } catch (error) {
        console.error('Eroare la incarcarea pachetelor:', error);
        setPackages([]);
      }

      setLoading(false);
    };

    loadServices();
  }, [selectedCategory, selectedStatus, user?.clinicId]);

  const getStatusColor = (active: boolean) => {
    return active ? 'success' : 'error';
  };

  const getStatusLabel = (active: boolean) => {
    return active ? 'Activ' : 'Inactiv';
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateService = () => {
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const [newServiceData, setNewServiceData] = useState({
    name: '',
    category: 'consultation',
    durationMin: 30,
    price: 0,
    description: ''
  });

  const handleSaveService = async () => {
    try {
      if (!user?.clinicId) {
        alert('Nu s-a putut identifica clinica!');
        return;
      }

      if (!newServiceData.name || !newServiceData.category || newServiceData.price <= 0) {
        alert('Completeaza toate campurile obligatorii!');
        return;
      }

      // Salvare in backend prin servicesService
      const serviceData = {
        clinicId: user.clinicId,
        name: newServiceData.name,
        code: `SRV-${Date.now()}`,
        description: newServiceData.description,
        category: newServiceData.category,
        duration: newServiceData.durationMin,
        basePrice: newServiceData.price,
        vatRate: 19,
        materials: [],
        labRequired: false,
        rules: [],
        portalVisibility: true,
        isActive: true
      };

      await servicesService.createService(serviceData);
      
      // Reincarca lista de servicii
      const servicesResponse = await servicesService.getServices({
        clinicId: user.clinicId
      });
      const servicesArray = servicesResponse.services || [];
      const transformedServices: Service[] = servicesArray.map((srv: any) => ({
        id: srv.id,
        name: srv.name || '',
        category: srv.category || 'Altele',
        durationMin: srv.duration || srv.durationMin || 30,
        price: srv.basePrice || srv.price || 0,
        vat: srv.vat || 19,
        active: srv.active !== undefined ? srv.active : srv.status === 'active',
        eligibleDoctorIds: srv.eligibleDoctorIds || [],
        eligibleLocationIds: srv.eligibleLocationIds || [],
        description: srv.description || '',
        createdAt: srv.createdAt || srv.created_at || new Date().toISOString(),
        updatedAt: srv.updatedAt || srv.updated_at || new Date().toISOString()
      }));
      setServices(transformedServices);
      
      setCreateDialogOpen(false);
      setNewServiceData({
        name: '',
        category: 'consultation',
        durationMin: 30,
        price: 0,
        description: ''
      });
      
      alert('Serviciu creat cu succes!');
      
    } catch (error) {
      console.error('Eroare la salvarea serviciului:', error);
      alert('Eroare la crearea serviciului!');
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || service.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || 
                         (selectedStatus === 'active' && service.active) ||
                         (selectedStatus === 'inactive' && !service.active);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || 
                         (selectedStatus === 'active' && pkg.active) ||
                         (selectedStatus === 'inactive' && !pkg.active);
    
    return matchesSearch && matchesStatus;
  });

  const activeServices = filteredServices.filter(s => s.active);
  const inactiveServices = filteredServices.filter(s => !s.active);
  const allServices = filteredServices;

  const activePackages = filteredPackages.filter(p => p.active);
  const inactivePackages = filteredPackages.filter(p => !p.active);
  const allPackages = filteredPackages;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Se incarca serviciile...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Servicii & Pachete
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Catalog servicii/pachete - CRUD servicii (pret, durata, TVA, categorie), pachete (bundle cu discount)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateService}
          sx={{ borderRadius: 2 }}
        >
          Serviciu Nou
        </Button>
      </Box>

      {/* Filtre */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                placeholder="Cauta serviciu sau categorie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Categorie</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Categorie"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="Consultatii">Consultatii</MenuItem>
                  <MenuItem value="Vaccinari">Vaccinari</MenuItem>
                  <MenuItem value="Chirurgie">Chirurgie</MenuItem>
                  <MenuItem value="Analize">Analize</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="active">Activ</MenuItem>
                  <MenuItem value="inactive">Inactiv</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                fullWidth
                sx={{ height: 56, borderRadius: 2 }}
              >
                Filtre Avansate
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Tooltip title="Actualizeaza">
                <IconButton onClick={() => window.location.reload()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="services tabs">
            <Tab 
              label={
                <Badge badgeContent={activeServices.length} color="success">
                  Servicii Active
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={activePackages.length} color="info">
                  Pachete Active
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={inactiveServices.length + inactivePackages.length} color="warning">
                  Inactive
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={allServices.length + allPackages.length} color="primary">
                  Toate
                </Badge>
              } 
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Serviciu</TableCell>
                  <TableCell>Categorie</TableCell>
                  <TableCell>Durata</TableCell>
                  <TableCell>Pret</TableCell>
                  <TableCell>TVA</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeServices
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((service) => (
                    <TableRow key={service.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.light' }}>
                            <SettingsIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {service.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {service.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={service.category} size="small" color="secondary" />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimerIcon fontSize="small" />
                          <Typography variant="body2">
                            {service.durationMin} min
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {service.price} RON
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {service.vat}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(service.active)}
                          color={getStatusColor(service.active) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Editeaza">
                            <IconButton size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Vezi detalii">
                            <IconButton size="small">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Dezactiveaza">
                            <IconButton size="small">
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={activeServices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Pachet</TableCell>
                  <TableCell>Servicii</TableCell>
                  <TableCell>Pret Total</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell>Pret Final</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activePackages
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((pkg) => (
                    <TableRow key={pkg.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'info.light' }}>
                            <ReceiptIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {pkg.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {pkg.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {pkg.items.length} servicii
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pkg.items.map(item => item.serviceName).join(', ')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {pkg.totalPrice} RON
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="success.main">
                          -{pkg.discount}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {pkg.totalPrice * (1 - pkg.discount / 100)} RON
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(pkg.active)}
                          color={getStatusColor(pkg.active) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Editeaza">
                            <IconButton size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Vezi detalii">
                            <IconButton size="small">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Dezactiveaza">
                            <IconButton size="small">
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={activePackages.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nume</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Detalii</TableCell>
                  <TableCell>Pret</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...inactiveServices, ...inactivePackages]
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'warning.light' }}>
                            {item.hasOwnProperty('items') ? <ReceiptIcon /> : <SettingsIcon />}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {item.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.hasOwnProperty('items') ? 'Pachet' : 'Serviciu'} 
                          size="small" 
                          color="warning" 
                        />
                      </TableCell>
                      <TableCell>
                        {item.hasOwnProperty('items') ? (
                          <Typography variant="body2">
                            {(item as ServicePackage).items.length} servicii
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            {(item as Service).category} • {(item as Service).durationMin} min
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {item.hasOwnProperty('items') 
                            ? `${(item as ServicePackage).totalPrice * (1 - (item as ServicePackage).discount / 100)} RON`
                            : `${(item as Service).price} RON`
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(item.active)}
                          color={getStatusColor(item.active) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Editeaza">
                            <IconButton size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Vezi detalii">
                            <IconButton size="small">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Activeaza">
                            <IconButton size="small">
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={inactiveServices.length + inactivePackages.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nume</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Detalii</TableCell>
                  <TableCell>Pret</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...allServices, ...allPackages]
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ 
                            bgcolor: item.active ? 'primary.light' : 'warning.light'
                          }}>
                            {item.hasOwnProperty('items') ? <ReceiptIcon /> : <SettingsIcon />}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {item.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.hasOwnProperty('items') ? 'Pachet' : 'Serviciu'} 
                          size="small" 
                          color={item.active ? 'primary' : 'warning'} 
                        />
                      </TableCell>
                      <TableCell>
                        {item.hasOwnProperty('items') ? (
                          <Typography variant="body2">
                            {(item as ServicePackage).items.length} servicii
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            {(item as Service).category} • {(item as Service).durationMin} min
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {item.hasOwnProperty('items') 
                            ? `${(item as ServicePackage).totalPrice * (1 - (item as ServicePackage).discount / 100)} RON`
                            : `${(item as Service).price} RON`
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(item.active)}
                          color={getStatusColor(item.active) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Editeaza">
                            <IconButton size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Vezi detalii">
                            <IconButton size="small">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {item.active ? (
                            <Tooltip title="Dezactiveaza">
                              <IconButton size="small">
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Activeaza">
                              <IconButton size="small">
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={allServices.length + allPackages.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>
      </Card>

      {/* Create Service Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle>Serviciu Nou</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nume Serviciu *"
                value={newServiceData.name}
                onChange={(e) => setNewServiceData({...newServiceData, name: e.target.value})}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Categorie</InputLabel>
                <Select
                  value={newServiceData.category}
                  onChange={(e) => setNewServiceData({...newServiceData, category: e.target.value})}
                  label="Categorie"
                >
                  <MenuItem value="consultation">Consultatie</MenuItem>
                  <MenuItem value="surgery">Chirurgie</MenuItem>
                  <MenuItem value="vaccination">Vaccinare</MenuItem>
                  <MenuItem value="dental">Stomatologie</MenuItem>
                  <MenuItem value="diagnostic">Diagnostic</MenuItem>
                  <MenuItem value="grooming">Ingrijire</MenuItem>
                  <MenuItem value="emergency">Urgenta</MenuItem>
                  <MenuItem value="other">Altele</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Durata (minute) *"
                type="number"
                value={newServiceData.durationMin}
                onChange={(e) => setNewServiceData({...newServiceData, durationMin: parseInt(e.target.value) || 0})}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Pret (RON) *"
                type="number"
                value={newServiceData.price}
                onChange={(e) => setNewServiceData({...newServiceData, price: parseFloat(e.target.value) || 0})}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Descriere"
                multiline
                rows={3}
                value={newServiceData.description}
                onChange={(e) => setNewServiceData({...newServiceData, description: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Anuleaza</Button>
          <Button 
            onClick={handleSaveService} 
            variant="contained" 
            color="primary"
            disabled={!newServiceData.name || newServiceData.price <= 0}
          >
            Salveaza Serviciu
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServicesManagement;

